import React, { useEffect, useState } from 'react';
import { MultipleQuestionnairesBlock } from '@coog/react-components';
import PropTypes from 'prop-types';
import { find, forEach, get, has, includes, isEmpty, isEqual, map, omit, omitBy, reduce } from 'lodash-es';
import Action from './Action.presentational';
import { getDurationInMonths } from '../../../Utils/Utils';

const LoanPresentational = ({
  blockLoans,
  isFinishedSalesRoute,
  partialUpdateSalesRouteAnswers,
  strings,
  cancelSalesRoute,
  putOnHoldSalesRoute,
  editField,
  willResetForm,
  onResetForm,
  willSaveForm,
  onSaveForm,
  noSaveNotValid,
  i18n,
}) => {
  // loanBlock
  const [isValidLoanBlock, setIsValidLoanBlock] = useState(false);
  const [answersLoanBlock, setAnswersLoanBlock] = useState();
  const [errorValidState, setErrorValidState] = useState({});
  let saveAnswersState = {};
  let isErrorValid = {};

  // console.log('answersLoanBlock', answersLoanBlock);

  const handleChangeIsValidLoanBlock = ({ isValid: isValidLocal, answers }, id) => {
    let tmpIsValid;
    let tmpErrors = !isEmpty(isErrorValid) ? isErrorValid : errorValidState;

    if (isValidLocal === null && id != null) {
      tmpIsValid = reduce(
        isValidLoanBlock,
        (acc, value, key) => {
          if (key.indexOf(id) < 0) {
            acc[key] = value;
          }

          return acc;
        },
        {}
      );
    } else {
      tmpIsValid = { ...isValidLoanBlock };

      forEach(answers, (answer, loanId) => {
        // If loan kind is no more graduated, remove increments from valid
        if (find(get(answer, 'parts[0].answers', []), { code: 'loan_kind' })?.value !== 'graduated') {
          tmpIsValid = omitBy(
            tmpIsValid,
            (value, key) => includes(key, `graduated_${loanId}_`) || key === `errors_${loanId}`
          );
          tmpErrors = omit(tmpErrors, [loanId]);
        }
      });

      tmpIsValid = { ...tmpIsValid, ...isValidLocal };
    }

    // Check if there's no error in graduated loans
    tmpIsValid = reduce(
      tmpErrors,
      (acc, isValid, key) => {
        if (has(tmpIsValid, key)) {
          acc[`errors_${key}`] = isValid;
        }
        return acc;
      },
      tmpIsValid
    );

    if (!isEqual(tmpIsValid, isValidLoanBlock)) {
      setIsValidLoanBlock(tmpIsValid);
    }
  };

  const handleChangeAnswersLoanBlock = (answersLocal, id) => {
    let tmpAnswers;
    let answersState = answersLoanBlock;

    if (answersLocal === null && id != null) {
      tmpAnswers = reduce(
        answersState,
        (acc, value, key) => {
          if (key.indexOf(id) < 0) {
            acc[key] = value;
          }

          return acc;
        },
        {}
      );
    } else {
      if (
        !answersLoanBlock ||
        (answersLoanBlock && Object.keys(saveAnswersState).length > Object.keys(answersLoanBlock).length)
      ) {
        answersState = saveAnswersState;
      }

      tmpAnswers = { ...answersState };

      forEach(answersLocal, (answer, loanId) => {
        // If loan kind is no more graduated, remove increments from answers
        if (find(get(answer, 'parts[0].answers', []), { code: 'loan_kind' })?.value !== 'graduated') {
          tmpAnswers = omitBy(tmpAnswers, (value, key) => includes(key, `graduated_${loanId}_`));
        }
      });

      tmpAnswers = { ...tmpAnswers, ...answersLocal };
    }

    if (!isEqual(tmpAnswers, answersState)) {
      saveAnswersState = tmpAnswers;
      setAnswersLoanBlock(tmpAnswers);
    }
  };

  const handleEditField = (answersLocal, id) => {
    if (answersLoanBlock) {
      if (answersLocal === null && id != null) {
        editField();
      } else {
        const tmpAnswers = { ...answersLoanBlock, ...answersLocal };

        if (!isEqual(tmpAnswers, answersLoanBlock)) {
          editField();
        }
      }
    }
  };

  const onResetFormLocal = data => {
    const answersReset = {};
    const isValidReset = {};
    forEach(
      map(data, l => l.id),
      id => {
        if (answersLoanBlock[id]) {
          answersReset[id] = answersLoanBlock[id];
        }
        if (isValidLoanBlock[id]) {
          isValidReset[id] = isValidLoanBlock[id];
        }
      }
    );
    setAnswersLoanBlock(answersReset);
    setIsValidLoanBlock(isValidReset);

    onResetForm();
  };

  const checkIsValid = reduce(isValidLoanBlock, (acc, isValid) => acc && isValid, true);

  /**
   * check disable next button
   */
  const checkDisableNextButton = () => {
    let disableNextButton;
    if (isFinishedSalesRoute) {
      disableNextButton = true;
    } else {
      disableNextButton = !checkIsValid;
    }
    return disableNextButton;
  };

  useEffect(() => {
    if (willSaveForm) {
      if (checkIsValid) {
        partialUpdateSalesRouteAnswers({ answers: answersLoanBlock, saveOnly: true })();
        onSaveForm();
      } else {
        noSaveNotValid();
      }
    }
  }, [willSaveForm]);

  const validateForm = stateBlock => (loanId, values) => {
    const errors = {};
    let loan;
    const graduateds = {};

    forEach(stateBlock, (someAnswers, key) => {
      if (key.indexOf('graduated_') >= 0) {
        const keySplit = key.split('_');

        if (!graduateds[keySplit[1]]) {
          graduateds[keySplit[1]] = {};
        }

        graduateds[keySplit[1]][keySplit[2]] = someAnswers;
      } else if (key === loanId) {
        loan = someAnswers;
      }
    });

    const loanAnswers = get(loan, 'parts[0].answers', []);
    isErrorValid = { ...errorValidState };

    if (find(loanAnswers, { code: 'loan_kind' })?.value === 'graduated') {
      const sumObj = reduce(
        get(graduateds, `[${loanId}]`, []),
        (acc, graduated, id) => {
          if (`graduated_${loanId}_${id}` !== values.id) {
            // Get data from answers or from state
            const dataGraduated = get(values, `answers[graduated_${loanId}_${id}]`, graduated);
            // Get answers
            const answersGraduated = get(dataGraduated, 'parts[0].answers', []);

            // Get duration in months
            const duration = getDurationInMonths({
              duration: parseInt(find(answersGraduated, { code: 'number_of_payments' })?.value, 10),
              durationUnit: find(answersGraduated, { code: 'payment_frequency' })?.value,
            });

            acc.duration += duration || 0;
            acc.amount += parseFloat(find(answersGraduated, { code: 'payment_amount' })?.value, 10) * duration || 0;

            return acc;
          }

          return acc;
        },
        { duration: 0, amount: 0 }
      );

      // Get loan duration in months
      const loanDuration = getDurationInMonths({
        duration: parseInt(find(loanAnswers, { code: 'duration' })?.value, 10) || 0,
        durationUnit: find(loanAnswers, { code: 'duration_unit' })?.value,
      });
      const loanAmount = parseFloat(find(loanAnswers, { code: 'amount' })?.value, 10) || 0;

      if (sumObj.amount > loanAmount) {
        errors.payment_amount = get(
          strings,
          'editsalesroute.error.message.graduated_amount',
          'Le montant total des paliers doit être inférieur ou égal au montant du prêt'
        );
      } else if (sumObj.duration !== loanDuration) {
        errors.number_of_payments = get(
          strings,
          'editsalesroute.error.message.graduated_duration',
          'La durée totale des paliers doit être égale à la durée du prêt'
        );
      }

      isErrorValid[loanId] = isEmpty(errors);
    } else {
      isErrorValid = omit(isErrorValid, loanId);
    }

    setErrorValidState({ ...errorValidState, ...isErrorValid });

    return errors;
  };

  // label Button
  const labelNextButton = get(strings, 'editsalesroute.action.button.nextstep.label', 'Etape Suivante');
  const labelCancelButton = get(strings, 'editsalesroute.action.button.cancel.label', 'Annuler');
  const labelPutonholdButton = get(strings, 'editsalesroute.action.button.putonhold.label', 'Mettre en attente');

  return (
    <>
      <MultipleQuestionnairesBlock
        blockData={blockLoans}
        blockType="loans"
        eventEmitter={event => {
          handleChangeIsValidLoanBlock(event, get(event, 'id'));
          handleChangeAnswersLoanBlock(get(event, 'answers'), get(event, 'id'));
          handleEditField(get(event, 'answers'), get(event, 'id'));
        }}
        strings={strings}
        willResetForm={willResetForm}
        onResetForm={onResetFormLocal}
        validateFormGraduatedLoans={validateForm(answersLoanBlock)}
        i18n={i18n}
      />
      <Action
        isDisabledNextButton={checkDisableNextButton()}
        partialUpdateSalesRoute={partialUpdateSalesRouteAnswers({ answers: answersLoanBlock })}
        cancelSalesRoute={cancelSalesRoute}
        putOnHoldSalesRoute={putOnHoldSalesRoute}
        labelNextButton={labelNextButton}
        labelCancelButton={labelCancelButton}
        labelPutonholdButton={labelPutonholdButton}
      />
    </>
  );
};

LoanPresentational.propTypes = {
  blockLoans: PropTypes.object.isRequired, // eslint-disable-line
  isFinishedSalesRoute: PropTypes.bool, // eslint-disable-line
  partialUpdateSalesRouteAnswers: PropTypes.func.isRequired,
  cancelSalesRoute: PropTypes.func.isRequired,
  putOnHoldSalesRoute: PropTypes.func.isRequired,
  strings: PropTypes.objectOf(PropTypes.string),
  editField: PropTypes.func.isRequired,
  willResetForm: PropTypes.bool,
  onResetForm: PropTypes.func,
  willSaveForm: PropTypes.bool,
  onSaveForm: PropTypes.func,
  noSaveNotValid: PropTypes.func,
  i18n: PropTypes.object,
};

LoanPresentational.defaultProps = {
  isFinishedSalesRoute: false,
  strings: {},
  willResetForm: false,
  onResetForm: () => {},
  willSaveForm: false,
  onSaveForm: () => {},
  noSaveNotValid: () => {},
  i18n: {},
};

export default LoanPresentational;
